import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { API_URL, getImageUrl } from '../../utils/api';

const ImageCarousel = ({ images = [], alt = "Plant Image", className = "", aspectRatio = "aspect-[4/3]", showThumbnails = true }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [loadedImages, setLoadedImages] = useState({});

    useEffect(() => {
        setCurrentIndex(0);
    }, [images]);

    if (!images || images.length === 0) {
        return (
            <div className={`w-full ${aspectRatio} bg-gray-100 dark:bg-gray-800 rounded-2xl flex flex-col items-center justify-center text-gray-400 ${className}`}>
                <ImageIcon className="w-12 h-12 mb-2 opacity-50" />
                <span className="text-sm font-medium">Kein Bild verfügbar</span>
            </div>
        );
    }

    const activeIndex = currentIndex < images.length ? currentIndex : 0;
    const currentImage = images[activeIndex];
    
    // Fallback if image array contains invalid entries
    if (!currentImage) {
        return (
            <div className={`w-full ${aspectRatio} bg-gray-100 dark:bg-gray-800 rounded-2xl flex flex-col items-center justify-center text-gray-400 ${className}`}>
                <ImageIcon className="w-12 h-12 mb-2 opacity-50" />
                <span className="text-sm font-medium">Lade Bild...</span>
            </div>
        );
    }
    
    const imageUrl = getImageUrl(currentImage.image);

    const handleNext = (e) => {
        e?.stopPropagation();
        if (images.length > 1) {
            setCurrentIndex((prev) => (prev + 1) % images.length);
        }
    };

    const handlePrev = (e) => {
        e?.stopPropagation();
        if (images.length > 1) {
            setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
        }
    };

    const handleMainClick = (e) => {
        // If thumbnails are hidden, we want the whole image to be clickable to cycle
        if (!showThumbnails && images.length > 1) {
            handleNext(e);
        }
    };

    const handleThumbnailClick = (e, index) => {
        e?.stopPropagation();
        setCurrentIndex(index);
    };

    return (
        <div className={`flex flex-col gap-2 w-full h-full min-h-0 ${className}`} onClick={(e) => e.stopPropagation()}>
            {/* Main Image Stage */}
            <div
                className={`relative w-full flex-1 min-h-0 bg-gray-900 rounded-2xl overflow-hidden shadow-sm group border border-gray-100 dark:border-gray-800 ${!showThumbnails && images.length > 1 ? 'cursor-pointer' : ''}`}
                onClick={handleMainClick}
            >
                <img
                    src={imageUrl}
                    alt={`${alt} - View ${activeIndex + 1}`}
                    className="w-full h-full object-cover transition-opacity duration-300"
                    onLoad={() => setLoadedImages(prev => ({ ...prev, [activeIndex]: true }))}
                />

                {!loadedImages[activeIndex] && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-800 animate-pulse">
                        <ImageIcon className="w-8 h-8 text-gray-400" />
                    </div>
                )}

                {/* Gradient overlay for text readability if needed, or just aesthetics */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />

                {/* Subtle License Imprint */}
                <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/60 backdrop-blur-md rounded text-white/70 text-[8px] font-medium tracking-wide">
                    © Wikipedia / CC-BY-SA
                </div>

                {/* Navigation Arrows */}
                {images.length > 1 && showThumbnails && (
                    <>
                        <button
                            onClick={handlePrev}
                            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/20 hover:bg-black/40 text-white backdrop-blur-md opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110"
                        >
                            <ChevronLeft className="w-6 h-6" />
                        </button>
                        <button
                            onClick={handleNext}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/20 hover:bg-black/40 text-white backdrop-blur-md opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-110"
                        >
                            <ChevronRight className="w-6 h-6" />
                        </button>
                    </>
                )}

                {/* Counter Badge */}
                {images.length > 1 && (
                    <div className="absolute top-3 right-3 px-2 py-1 bg-black/40 backdrop-blur-md text-white text-xs font-bold rounded-lg pointer-events-none">
                        {activeIndex + 1} / {images.length}
                    </div>
                )}

                {/* Tap Hint if thumbnails are hidden */}
                {!showThumbnails && images.length > 1 && (
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/20 backdrop-blur-md text-white/60 text-[8px] font-black uppercase tracking-widest rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                        Tippen für nächstes Bild
                    </div>
                )}
            </div>

            {/* Thumbnail Strip */}
            {images.length > 1 && showThumbnails && (
                <div className="flex-shrink-0 flex gap-2 overflow-x-auto pb-1 scrollbar-hide snap-x px-1">
                    {images.map((img, idx) => {
                        const thumbUrl = getImageUrl(img.image);
                        return (
                            <button
                                key={idx}
                                onClick={(e) => handleThumbnailClick(e, idx)}
                                className={`relative flex-shrink-0 w-20 h-16 rounded-xl overflow-hidden transition-all duration-300 snap-start bg-gray-50 dark:bg-gray-800 border-2 ${activeIndex === idx
                                    ? 'border-plantlix-500 shadow-md opacity-100 scale-105'
                                    : 'border-transparent opacity-60 hover:opacity-100'
                                    }`}
                            >
                                <img
                                    src={thumbUrl}
                                    alt={`Thumbnail ${idx + 1}`}
                                    className="w-full h-full object-contain p-1"
                                />
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default ImageCarousel;
